import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { CaseloadReading, clientDisplayName } from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK } from './agencyChartTheme';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { ClientReadingAvatar } from './ClientReadingAvatar';
import { IWorthWatchingRow } from './InsightsNarration';

const useStyles = createUseStyles({
    rows: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 2
    },

    row: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 8px',
        background: 'none',
        border: 0,
        borderTop: `1px solid ${BRAND.tealEdge}`,
        borderRadius: 8,
        font: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:first-child': {
            borderTop: 'none'
        },
        '&:hover': {
            background: BRAND.tealWash
        },

        '@media print': {
            breakInside: 'avoid'
        }
    },

    body: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 3,
        flex: 1,
        minWidth: 0
    },

    name: {
        fontSize: 13.5,
        fontWeight: 500,
        color: BRAND.deepTeal
    },

    why: {
        fontSize: 13,
        lineHeight: 1.45,
        color: CHART_INK.strongLabel
    },

    quiet: {
        fontSize: 12.5,
        color: CHART_INK.label
    },

    more: {
        marginTop: 10,
        fontSize: 12.5,
        color: CHART_INK.label
    }
});

/**
 * The worklist: who is worth watching and why, in the findings' own words, each row a way into that
 * client's detail view.
 */
export const WorthWatchingList: FC<{
    rows: IWorthWatchingRow[];
    /** Clients who matched but did not fit, so a capped worklist cannot read as complete. */
    unlistedCount: number;
    /** Whether the rows were already narrowed, which changes what an empty list means. */
    narrowed: boolean;
    /** How each client is drawn, with the audited link to a hosted photo. */
    clientAvatar: (careMapId: string) => IAvatarIdentity | undefined;
    clientReading: (careMapId: string) => CaseloadReading | undefined;
    onSelectClient: (careMapId: string) => void;
}> = ({ rows, unlistedCount, narrowed, clientAvatar, clientReading, onSelectClient }) => {
    const classes = useStyles();

    if (rows.length === 0) {
        return (
            <div className={classes.quiet} data-id="worth-watching-empty">
                {/* No unreadable caveat here: on screen the Bea line beneath this list carries
                    it, and on paper the band at the top of the sheet does. */}
                {narrowed
                    ? 'Nothing in this selection.'
                    : 'Nobody is worth watching across the caseload right now.'}
            </div>
        );
    }

    return (
        <>
            <div className={classes.rows}>
                {rows.map(row => (
                    <button
                        key={row.careMapId}
                        type="button"
                        className={classes.row}
                        data-id="worth-watching-row"
                        data-incident={String(row.hasIncident)}
                        onClick={() => onSelectClient(row.careMapId)}
                    >
                        <ClientReadingAvatar size={28} identity={clientAvatar(row.careMapId)} name={clientDisplayName(row.clientName)} reading={clientReading(row.careMapId)} />
                        <span className={classes.body}>
                            <span className={`${classes.name} mp-mask`}>{clientDisplayName(row.clientName)}</span>
                            {/* Masked, unlike a note's own text, because a finding can name a family member along with the client. */}
                            <span className={`${classes.why} mp-mask`}>{row.why}</span>
                        </span>
                    </button>
                ))}
            </div>

            {unlistedCount > 0 && (
                <div className={classes.more} data-id="worth-watching-more">
                    {unlistedCount} more {unlistedCount === 1 ? 'client is' : 'clients are'} not shown.
                </div>
            )}
        </>
    );
};
